import React from 'react';
import { Button, RowActions } from '../../../components/ui';
import { materialCertificateService } from '../../../services/materialCertificateService';
import type { MaterialCertificate } from '../types';
import { certificateHeadline, formatCertificateDate } from '../utils/certificates';

/** Why the attach and remove actions are switched off. Shown, not hidden (design-system.md §5). */
export const CERTIFICATES_NO_PERMISSION_REASON = 'You do not have permission to manage quality certificates.';

export type CertificatesLoad = 'loading' | 'error' | 'ready';

interface BinCertificatesProps {
  binLabel: string;
  /** The material the bin holds now; a certificate for something else it used to hold says so. */
  currentMaterialId: string | null;
  certificates: MaterialCertificate[];
  load: CertificatesLoad;
  /** Omit to show the actions switched off with the reason. */
  onAttach?: () => void;
  onRemove?: (certificate: MaterialCertificate) => void;
}

/**
 * The quality certificates on one bin, every one it has had, newest date first. Each leads with
 * the date on it and its description, then the file. Nothing is hidden for being old: an audit
 * asks what was certified at the time, not only what is certified now.
 */
const BinCertificates: React.FC<BinCertificatesProps> = ({
  binLabel,
  currentMaterialId,
  certificates,
  load,
  onAttach,
  onRemove,
}) => (
  <section aria-label={`Quality certificates on ${binLabel}`} className="tw-space-y-2 tw-px-4 tw-py-3">
    <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2">
      <h3 className="tw-mb-0 tw-text-sm tw-font-semibold tw-text-content-secondary">
        Quality Certificates
        {load === 'ready' ? <span className="tw-ml-1 tw-font-normal tw-text-content-muted">({certificates.length})</span> : null}
      </h3>
      <Button
        size="sm"
        variant="outline-primary"
        disabled={!onAttach}
        disabledReason={onAttach ? undefined : CERTIFICATES_NO_PERMISSION_REASON}
        onClick={() => onAttach?.()}
      >
        <i className="bi bi-paperclip" aria-hidden="true" />
        Attach certificate
      </Button>
    </div>

    {load === 'loading' ? (
      <p className="tw-mb-0 tw-text-sm tw-text-content-muted">Loading certificates…</p>
    ) : load === 'error' ? (
      <p className="tw-mb-0 tw-text-sm tw-text-danger">Certificates could not be loaded. Reload to try again.</p>
    ) : certificates.length === 0 ? (
      <p className="tw-mb-0 tw-text-sm tw-text-content-muted">No certificates on {binLabel} yet.</p>
    ) : (
      <ul className="tw-mb-0 tw-list-none tw-divide-y tw-divide-line tw-rounded tw-border tw-border-line tw-bg-surface tw-p-0">
        {certificates.map((certificate) => {
          const headline = certificateHeadline(certificate.description);
          return (
            <li
              key={certificate.id}
              data-testid={`certificate-${certificate.id}`}
              className="tw-flex tw-items-start tw-justify-between tw-gap-3 tw-px-3 tw-py-2"
            >
              <div className="tw-min-w-0 tw-flex-1">
                <p className="tw-mb-0 tw-text-xs tw-font-semibold tw-tabular-nums tw-text-content-secondary">
                  {formatCertificateDate(certificate.issuedAt)}
                  {certificate.reference ? <span className="tw-ml-2 tw-font-normal tw-text-content-muted">#{certificate.reference}</span> : null}
                </p>
                <p className="tw-mb-0 tw-whitespace-pre-line tw-break-words tw-text-sm tw-text-content">{certificate.description}</p>
                {currentMaterialId && certificate.materialId !== currentMaterialId ? (
                  <p className="tw-mb-0 tw-text-xs tw-text-warning-text">For {certificate.materialLabel}, which this bin held before.</p>
                ) : null}
                <p className="tw-mb-0 tw-mt-0.5 tw-text-xs tw-text-content-muted">
                  <a
                    href={materialCertificateService.fileUrl(certificate.id)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="tw-text-primary hover:tw-underline"
                  >
                    {certificate.fileName}
                  </a>
                  {certificate.uploadedBy ? ` · uploaded by ${certificate.uploadedBy}` : ''}
                </p>
              </div>
              <RowActions
                actions={[
                  {
                    kind: 'download',
                    label: `Open ${headline}`,
                    onClick: () => window.open(materialCertificateService.fileUrl(certificate.id), '_blank', 'noopener'),
                  },
                  {
                    kind: 'delete',
                    label: onRemove ? `Remove ${headline} from ${binLabel}` : `Remove – ${CERTIFICATES_NO_PERMISSION_REASON.toLowerCase()}`,
                    onClick: () => onRemove?.(certificate),
                    disabled: !onRemove,
                  },
                ]}
              />
            </li>
          );
        })}
      </ul>
    )}
  </section>
);

export default BinCertificates;
